<template>
  <div
    class="kk-badge"
    :class="[`kk-badge--${theme}`, `kk-badge--${dotSize}`]"
  >
    <span
      v-if="formattedValue !== null"
      ref="textRef"
      class="kk-badge__text"
      :style="{
        'margin-left': negativeMarginX,
        'margin-right': negativeMarginX,
      }"
    >
      {{ formattedValue }}
    </span>
  </div>
</template>

<script>
/**
 * @import { Ref } from 'vue';
 */

import { onMounted, onUnmounted, ref, shallowRef } from 'vue';

export const BADGE_THEME_LIST = [
  'system',
  'marketing-state',
  'marketing-date',
  'marketing-discount',
];

export const BADGE_DOT_SIZE_LIST = ['md', 'lg', 'xl'];

export default {
  name: 'KkBadge',
  props: {
    theme: {
      type: String,
      default: BADGE_THEME_LIST[0],
    },
    dotSize: {
      type: String,
      default: BADGE_DOT_SIZE_LIST[0],
    },
    value: {
      type: [String, Number],
      default: null,
    },
  },
  setup() {
    // 用來抵銷 transform scale 縮小文字時造成的左右空間
    const negativeMarginX = ref('0px');

    /** @type {Ref<HTMLElement | null>} */
    const textRef = ref(null);
    /** @type {Ref<IntersectionObserver | null>} */
    const observer = shallowRef(null);

    const updateNegativeMarginX = () => {
      if (!textRef.value) {
        return;
      }

      const textWidth = textRef.value.clientWidth;

      /** @type {number} */
      const transformScale = parseFloat(
        window
          .getComputedStyle(textRef.value)
          .getPropertyValue('--transform-scale'),
      );
      const marginX = Math.round((textWidth * (transformScale - 1)) / 2);
      negativeMarginX.value = `${marginX}px`;
    };

    onMounted(() => {
      if (!textRef.value) {
        return;
      }

      // 在 intersection observer 初始化前先計算一次 margin，避免 reflow
      updateNegativeMarginX();

      // 在 dropdown menu 打開時需要重新計算 margin
      observer.value = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          updateNegativeMarginX();
        }
      });

      observer.value.observe(textRef.value);
    });

    onUnmounted(() => {
      if (observer.value) {
        observer.value.disconnect();
      }
    });

    return {
      negativeMarginX,
      textRef,
      updateNegativeMarginX,
    };
  },

  computed: {
    formattedValue() {
      // empty string
      if (typeof this.value === 'string' && this.value.trim() === '') {
        return null;
      }

      // number larger than 99
      if (this.value > 99) {
        return '99+';
      }

      return this.value;
    },
  },
  watch: {
    formattedValue() {
      this.$nextTick(this.updateNegativeMarginX);
    },
  },
};
</script>
